import { useState, useRef, useEffect, useCallback } from 'react';
import GenericAccordian from '../../components/GenericAccordian/GenericAccordian';
import './DocumitraPartners.scss';
import { getFormDefaultValue, getTableHeaders } from '../../utils/forms/FormDataModel';
import { ContentItem, OnAddArguments, TabContent } from '../../utils/forms/FormInterface';
import AbstractForm from '../Entity/AbstractForm';
import AbstractLanding from '../Entity/AbstractLanding';
import AbstractTabs, { TabActiosEventType, TabActiosProps } from '../Entity/AbstractTabs';
import {
  DOCUMITRA_PARTNERS,
  COUNTRY_CODE,
  PARTNER_SERVICES,
  COUNTRY,
} from '../../constants/EntityName';
import { documitraPartnersFormData } from '../../datamodels/DocumitraPartners';
import PartnerAssignedPackages from './PartnerAssignedPackages';
import CreatePartnerPackages from './CreatePartnerPackages';
import callApi from '../../Api/config';
import AgentPaymentLedgerDetails from '../AgentPaymentLedger/AgentPaymentLedgerDetails';
import AgentOrderPaymentDetails from '../AgentPaymentLedger/AgentOrderPaymentDetails';
import { toastError, toastInfo, toastSuccess } from '../../service/Toast';

const additionalEntityName = [
  { name: COUNTRY_CODE, getAllResponse: true },
  { name: COUNTRY, getAllResponse: true },
];

// Create mode: hide auto-generated field inputs (partnerId) but KEEP the entry in the array
// so its title: 'Partner Information' still creates the accordion header.
// Setting isAccordian: false prevents the field's own input from rendering.
const createFormTemplate = documitraPartnersFormData.map((f: any) => {
  const field = f.hideInCreate ? { ...f, isAccordian: false } : f;

  return field.name === 'zelleId' ? { ...field, title: 'Payment Information' } : field;
});

// Edit mode: disable identity fields that must not be changed after the partner is created
const editFormTemplate = documitraPartnersFormData.map((f: any) =>
  f.editReadOnly ? { ...f, readonly: true } : f
);

// Computed once at module level — stable references prevent AbstractForm from resetting
// its form fields on re-renders triggered by state changes (e.g. adding packages).
const defaultFormValue: any = getFormDefaultValue(documitraPartnersFormData);
const tableHeaders: any = getTableHeaders(documitraPartnersFormData);

const DocumitraPartners = () => {
  const [activeKey, setActiveKey] = useState<string>('0');
  const [userName, setUserName] = useState<string>();
  const [showFormComponent, setShowFormComponent] = useState<boolean>(false);
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [content, setContent] = useState<TabContent>([]);
  const [packagesToAdd, setPackagesToAdd] = useState<any[]>([]);
  const packagesRef = useRef<any[]>([]);
  const submitRef = useRef<(() => void) | null>(null); // triggers AbstractForm submission from outside
  const ledgerRefreshers = useRef<Map<string, () => void>>(new Map());
  const orderTabParents = useRef<Map<string, string>>(new Map());

  // Keep ref in sync with state to avoid stale closures in callbacks
  useEffect(() => {
    packagesRef.current = packagesToAdd;
  }, [packagesToAdd]);

  const addName = 'Agent';
  const headerName = 'Documitra Partners';

  const onSuccessful = (id?: string) => {
    if (id === 'create') {
      setShowFormComponent(false);
    }
    onTabClick('0');
    setRefreshData(prev => !prev);
  };

  const handlePartnerCreation = async (id?: string, newPartnerId?: string) => {
    const currentPackages = packagesRef.current;
    console.log('handlePartnerCreation called', {
      id,
      newPartnerId,
      packagesCount: currentPackages.length,
    });

    if (id === 'create' && newPartnerId && currentPackages.length > 0) {
      try {
        toastInfo('Creating partner packages...');

        const promises = currentPackages.map((pkg: any) => {
          const payload = {
            partnerId: newPartnerId,
            packageId: pkg.packageId,
            customPrice: pkg.customPrice ? Number(pkg.customPrice) : undefined,
            documitraFee: pkg.documitraFee ? Number(pkg.documitraFee) : undefined,
            name: pkg.name || pkg.package?.name,
            status: 'Active',
          };
          console.log('Creating package payload:', payload);
          return callApi(PARTNER_SERVICES, 'POST', payload);
        });

        await Promise.all(promises);
        toastSuccess('Partner and packages created successfully!');
      } catch (error) {
        toastError('Partner created, but some packages failed to assign.');
        console.error('Package creation failed', error);
      }
    }
    onSuccessful(id);
  };

  const onAddNew = (argument1?: OnAddArguments) => {
    if (argument1) {
      const checkArr = content.filter((item: ContentItem) => item.key === argument1.id);
      checkArr.length ? null : onAdd(argument1);
    } else {
      setPackagesToAdd([]); // Reset packages
      setShowFormComponent(true);
      setUserName('Add Agent');
      onTabClick('a');
    }
  };

  const closeTab = (key: string, shouldRefresh: boolean) => {
    setContent(content => content.filter(item => item.key !== key));
    setActiveKey('0');
    shouldRefresh && setRefreshData(prev => !prev);
    if (key === 'a') {
      setShowFormComponent(false);
      setPackagesToAdd([]); // Reset packages
    }
  };

  const openOrderTab = useCallback((partnerId: string, order: any) => {
    if (!order?.id) return;
    const key = `order-${order.id}`;
    orderTabParents.current.set(key, partnerId);
    setContent((prev: TabContent) => {
      if (prev.some((item: ContentItem) => String(item.key) === key)) return prev;
      return [
        ...prev,
        {
          tabTitle: order.groupCode || String(order.id).slice(-6),
          key,
          content: (
            <AgentOrderPaymentDetails
              partnerId={partnerId}
              order={order}
              onPaymentUpdated={() => ledgerRefreshers.current.get(partnerId)?.()}
            />
          ),
        },
      ];
    });
    setActiveKey(key);
  }, []);

  const onAdd = (argument1: OnAddArguments) => {
    const partnerId = argument1.id;
    const partnerName = (argument1.partnerName as string) || 'Partner';
    const recordPartnerId = (argument1 as any).partnerId;
    const tabTitle = recordPartnerId || (argument1.id as string);

    const newObj = {
      key: argument1.id as string,
      tabTitle: tabTitle,
      content: (
        <div key={content.length}>
          <GenericAccordian>
            <AbstractForm
              entityId={partnerId}
              isCreateMode={false}
              headerName={headerName}
              entityName={DOCUMITRA_PARTNERS}
              // editFormTemplate: identity fields are disabled in edit mode
              formTemplate={editFormTemplate}
              formDefaultPayload={getFormDefaultValue(documitraPartnersFormData)}
              onSuccessfulUpdate={onSuccessful}
              formHeader=""
              showDeleteDraft={true}
              closeTab={closeTab}
              hiddenButtons={{ delete: true, cancel: true }}
              additionalEntityName={additionalEntityName}
              partnerAdditionalTab={
                <PartnerAssignedPackages partnerId={partnerId || ''} partnerName={partnerName} />
              }
              partnerPaymentLedgerTab={
                partnerId ? (
                  <AgentPaymentLedgerDetails
                    partnerId={partnerId}
                    hideAgentInfo
                    onOpenOrder={(order: any) => openOrderTab(partnerId, order)}
                    registerRefresh={(refresh: () => void) =>
                      ledgerRefreshers.current.set(partnerId, refresh)
                    }
                  />
                ) : undefined
              }
            />
          </GenericAccordian>
        </div>
      ),
    };

    // Make the newly added tab active
    setContent((prev: any) => [...prev, newObj]);
    argument1.id && setActiveKey(String(argument1.id));
  };

  const onTabClick = (key: string) => {
    setActiveKey(key);
  };

  const updateFormVisibility = (
    action: TabActiosProps,
    targetKey?: string | TabActiosEventType
  ) => {
    if (action === 'remove') {
      if (targetKey === 'a') {
        setShowFormComponent(false);
        setPackagesToAdd([]); // Reset packages
        onTabClick('0');
      } else {
        const key = String(targetKey);
        const parent = orderTabParents.current.get(key);
        orderTabParents.current.delete(key);
        onTabClick(parent || '0');
        setContent(content => content.filter(item => String(item.key) !== key));
      }
    }
  };

  return (
    <AbstractTabs
      activeKey={activeKey}
      entityName="Agent User Management"
      updateFormVisibility={updateFormVisibility}
      onTabClick={onTabClick}
      secondTabTitle={userName}
      showFormComponent={showFormComponent}
      content={content}
      formComponent={
        <GenericAccordian>
          <div className="documitra-partners-create">
            <AbstractForm
              isCreateMode={true}
              headerName={headerName}
              entityName={DOCUMITRA_PARTNERS}
              // createFormTemplate: partnerId field is hidden (auto-generated by backend)
              formTemplate={createFormTemplate}
              formDefaultPayload={defaultFormValue}
              onSuccessfulUpdate={handlePartnerCreation}
              closeTab={closeTab}
              additionalEntityName={additionalEntityName}
              // Hide built-in buttons — we render them below the packages section
              hiddenButtons={{ save: true, cancel: true }}
              externalSubmitRef={submitRef}
              partnerAdditionalTab={<CreatePartnerPackages onChange={setPackagesToAdd} />}
            />
            {/* Buttons placed BELOW the Assign Packages section */}
            <div className="documitra-partners-create__actions">
              <button
                type="button"
                disabled={packagesToAdd.length === 0}
                onClick={() => submitRef.current?.()}
                style={{
                  background: packagesToAdd.length === 0 ? '#b8c6d1' : '#004195',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 24px',
                  fontSize: '15px',
                  cursor: packagesToAdd.length === 0 ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  height: '44px',
                }}
              >
                Create
              </button>
              <button
                type="button"
                onClick={() => closeTab('a', false)}
                style={{
                  background: 'white',
                  color: '#172033',
                  border: '1px solid #ccd8e5',
                  borderRadius: '6px',
                  padding: '10px 24px',
                  fontSize: '15px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  height: '44px',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </GenericAccordian>
      }
    >
      <AbstractLanding
        headerName={headerName}
        addName={addName}
        entityName={DOCUMITRA_PARTNERS}
        tableHeaders={tableHeaders}
        onAdd={onAddNew}
        refreshData={refreshData}
        showFilter={true}
      />
    </AbstractTabs>
  );
};

export default DocumitraPartners;
